/**
 * PARÇA 437 — Public support page.
 *
 * Hosted at https://event.dayivo.com/support. REQUIRED for App Store
 * (Apple wants a Support URL on every submission).
 */
import React from 'react';

export function SupportPage(): React.JSX.Element {
  return (
    <main className="mx-auto max-w-3xl px-6 py-10 text-gray-900 dark:text-gray-100">
      <header className="mb-8">
        <h1 className="text-3xl font-semibold">Support</h1>
        <p className="mt-2 text-sm text-gray-500">We&apos;re here to help.</p>
      </header>

      <section className="space-y-8">
        <div>
          <h2 className="text-xl font-semibold">Get in touch</h2>
          <p className="mt-2">
            For general questions or issues: <a href="mailto:support@event.dayivo.com" className="text-blue-600 hover:underline dark:text-blue-400">support@event.dayivo.com</a>
          </p>
          <p className="mt-2">
            For privacy concerns or to request your data: <a href="mailto:privacy@event.dayivo.com" className="text-blue-600 hover:underline dark:text-blue-400">privacy@event.dayivo.com</a>
          </p>
          <p className="mt-2">
            Response time: usually within 48 hours.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-semibold">Common questions</h2>

          <details className="mt-4 rounded-md border border-gray-200 p-4 dark:border-gray-700">
            <summary className="cursor-pointer font-medium">How do I delete my account?</summary>
            <p className="mt-2 text-sm">
              Open the app → Settings → Privacy → Delete Account. Your account will be soft-deleted for 30 days (recovery window) and then permanently removed. If you can&apos;t access the app, email <a href="mailto:privacy@event.dayivo.com" className="text-blue-600 hover:underline dark:text-blue-400">privacy@event.dayivo.com</a>.
            </p>
          </details>

          <details className="mt-2 rounded-md border border-gray-200 p-4 dark:border-gray-700">
            <summary className="cursor-pointer font-medium">How do I export my data?</summary>
            <p className="mt-2 text-sm">
              Open the app → Settings → Privacy → Export My Data. You&apos;ll receive a downloadable archive of everything we hold about you within 30 days (typically much sooner).
            </p>
          </details>

          <details className="mt-2 rounded-md border border-gray-200 p-4 dark:border-gray-700">
            <summary className="cursor-pointer font-medium">How do I report a user, venue, or content?</summary>
            <p className="mt-2 text-sm">
              In the app, every profile, story, post, and comment has a flag icon. Tap it to report. Our moderation team reviews reports within 24 hours.
            </p>
          </details>

          <details className="mt-2 rounded-md border border-gray-200 p-4 dark:border-gray-700">
            <summary className="cursor-pointer font-medium">I&apos;m a venue owner. How do I claim my venue?</summary>
            <p className="mt-2 text-sm">
              Email <a href="mailto:support@event.dayivo.com" className="text-blue-600 hover:underline dark:text-blue-400">support@event.dayivo.com</a> from a verifiable business email with your venue&apos;s name and any documentation (business registration, lease, etc.). We typically verify within 3–5 business days.
            </p>
          </details>

          <details className="mt-2 rounded-md border border-gray-200 p-4 dark:border-gray-700">
            <summary className="cursor-pointer font-medium">I forgot my password.</summary>
            <p className="mt-2 text-sm">
              On the login screen, tap &quot;Forgot password&quot; and we&apos;ll email you a reset link. Or use the magic link login option.
            </p>
          </details>

          <details className="mt-2 rounded-md border border-gray-200 p-4 dark:border-gray-700">
            <summary className="cursor-pointer font-medium">Will you ever sell my data?</summary>
            <p className="mt-2 text-sm">
              No. Never. See our <a href="/privacy" className="text-blue-600 hover:underline dark:text-blue-400">Privacy Policy</a>.
            </p>
          </details>
        </div>

        <div>
          <h2 className="text-xl font-semibold">Service status</h2>
          <p className="mt-2">
            For outages or known issues, check our system status (coming soon). For urgent realtime problems, email support and we&apos;ll respond as fast as we can.
          </p>
        </div>
      </section>

      <footer className="mt-12 border-t border-gray-200 pt-6 text-sm text-gray-500 dark:border-gray-700">
        <a href="/" className="text-blue-600 hover:underline dark:text-blue-400">Back to home</a>
        <span className="mx-2">·</span>
        <a href="/privacy" className="text-blue-600 hover:underline dark:text-blue-400">Privacy Policy</a>
        <span className="mx-2">·</span>
        <a href="/terms" className="text-blue-600 hover:underline dark:text-blue-400">Terms of Service</a>
      </footer>
    </main>
  );
}
